import { createSlice } from '@reduxjs/toolkit'
import { invoicesState } from 'Admin/Store/InitialStates'
import { asyncQuery } from 'Utils/apolloClient'
import { asyncThunkMutationWithErrors, asyncThunkQueryWithErrors } from 'Admin/Store/asyncThunkHandler'
import { simulatedCards } from 'Utils/simulatedCards'
import installmentAttachEmailAndSend from 'Admin/Mutations/installmentAttachEmailAndSendMutation'
import installmentChangeOwedQuery from 'Admin/Queries/installmentChangeOwedQuery'
import installmentItemDeleteMutation from 'Admin/Queries/installmentItemDeleteMutation'
import installmentsQuery from 'Admin/Queries/installmentsQuery'
import installmentDetailsQuery from 'Admin/Queries/installmentDetailsQuery'
import installmentReceiptQuery from 'Admin/Queries/installmentReceiptQuery'
import installmentPaymentMutation from 'Admin/Mutations/installmentPaymentMutation'
import installmentReceiptMutation from 'Admin/Mutations/installmentReceiptMutation'
import installmentStateMutation from 'Admin/Queries/installmentStateMutation'

export const queryInstallments = asyncThunkQueryWithErrors(
  'invoices/queryInstallments',
  installmentsQuery,
  ({ data }) => {
    const { hasLateFeeConfig, isInventory, installments } = data?.campground
    const campground = { hasLateFeeConfig, isInventory }

    return { campground, installments }
  }
)

export const queryInstallmentDetails = asyncThunkQueryWithErrors(
  'invoices/queryInstallmentDetails',
  async ({ terminal, variables }) => {
    const { data, graphQLErrors } = await asyncQuery(installmentDetailsQuery)({ variables })
    const error = graphQLErrors && graphQLErrors[0]
    if (error) throw error

    return { campground: data.campground, isNotProduction: data.isNotProduction, terminal }
  }
)

export const queryInstallmentChangeOwed = asyncThunkQueryWithErrors(
  'invoices/queryInstallmentChangeOwed',
  installmentChangeOwedQuery,
  ({ data }) => data.campground?.installment
)

export const queryInstallmentReceipt = asyncThunkQueryWithErrors(
  'invoices/queryInstallmentReceipt',
  installmentReceiptQuery,
  ({ data }) => data.installment
)

export const mutationInstallmentState = asyncThunkMutationWithErrors(
  'invoices/mutationInstallmentState',
  installmentStateMutation,
  ({ action, data }) => ({ action, installment: data.installmentStateUpdate.installment })
)

export const mutationInstallmentItemDelete = asyncThunkMutationWithErrors(
  'invoices/mutationInstallmentItemDelete',
  installmentItemDeleteMutation,
  ({ data }) => data.installmentItemDelete.installment
)

export const mutationInstallmentPayment = asyncThunkMutationWithErrors(
  'invoices/mutationInstallmentPayment',
  installmentPaymentMutation,
  ({ data }) => data.installmentPayment.invoice
)

export const mutationInstallmentReceipt = asyncThunkMutationWithErrors(
  'invoices/mutationInstallmentReceipt',
  installmentReceiptMutation,
  ({ data }) => data.printInstallmentReceipt.installment
)

export const mutationInstallmentAttachEmailAndSend = asyncThunkMutationWithErrors(
  'invoices/mutationInstallmentAttachEmailAndSend',
  installmentAttachEmailAndSend,
  ({ action, data }) => ({ action, installment: data.installmentAttachEmailAndSend.installment })
)

const configureTerminal = ({ terminalReaderKey, isTerminalSimulation, terminalSimulation, useTerminalMock }) => {
  const testCardNumber = isTerminalSimulation ? simulatedCards[terminalSimulation] : null
  const useSimulation = !!testCardNumber
  const readerKey = useSimulation ? 'SIMULATOR' : terminalReaderKey
  const configured = useSimulation || !!readerKey
  const useMock = configured && useTerminalMock === 'true'

  return { ...invoicesState.campground.terminal, configured, readerKey, testCardNumber, useMock, useSimulation }
}


const invoices = createSlice({
  name: 'invoices',
  initialState: invoicesState,
  reducers: {
    fetchInstallmentStatus: state => {
      state.installmentDetails.fetchDetails = true
    },

    setInvoiceReplacedStatus: (state, { payload }) => {
      state.installmentDetails.invoiceReplaced = payload
    },

    setItem: (state, { payload }) => {
      state.item = payload
    },

    updateAction: (state, { payload }) => {
      state.installmentDetails.action = payload
    },

    updateFilterInvoiceStatus: (state, { payload }) => {
      state.filters.invoiceStatus = payload
      state.pagination.currentPage = 1
    },

    updateFilterPayoutStatus: (state, { payload }) => {
      state.filters.payoutStatus = payload
      state.pagination.currentPage = 1
    },

    updateFilterTerm: (state, { payload }) => {
      state.filters.term = payload
      state.pagination.currentPage = 1
    },

    updateInstallmentDetailsData: (state, { payload }) => {
      state.installmentDetails.action = 'refundInvoice'
      state.installmentDetails.data = payload
    },

    updatePageData: (state, { payload }) => {
      state.pagination = { ...state.pagination, ...payload }
    },

    updateParams: (state, { payload }) => {
      const { pagination: { currentPage } } = state
      const { itemsPerPage, page = 1, installmentStatus, payoutState, search, sortDirection, sortName, sortTable,
              type } = payload
      let newFilters = state.filters
      let newPagination = { currentPage, perPage: invoicesState.pagination.perPage }
      let newSortedField = state.sortedField

      if (itemsPerPage) newPagination = { ...newPagination, perPage: parseInt(itemsPerPage) }
      if (page) newPagination = { ...newPagination, currentPage: parseInt(page) }
      if (installmentStatus) {
        const newStatus = typeof installmentStatus === 'string' ? [installmentStatus] : installmentStatus
        newFilters = { ...newFilters, invoiceStatus: newStatus }
      }
      if (payoutState) newFilters = { ...newFilters, payoutStatus: payoutState }
      if (search) newFilters = { ...newFilters, term: search }
      if (type) newFilters = { ...newFilters, usedFor: type }
      if (sortDirection && sortName) {
        const table = sortTable && { table: sortTable }
        newSortedField = { direction: sortDirection, name: sortName, ...table }
      }

      state.filters = newFilters
      state.isParamsReviewed = true
      state.pagination = newPagination
      state.sortedField = newSortedField
    },

    updateSortField: (state, { payload }) => {
      state.pagination.currentPage = 1
      state.sortedField = payload
    },
  },

  extraReducers: {
    [queryInstallments.pending]: state => {
      state.installments.loading = true
    },
    [queryInstallments.fulfilled]: (state, { payload }) => {
      state.campground = payload.campground
      state.installments.data = payload.installments
      state.installments.loading = false
    },
    [queryInstallments.rejected]: (state, { payload }) => {
      state.installments.error = payload
      state.installments.loading = false
    },
    [queryInstallmentDetails.pending]: state => {
      state.installmentDetails.loading = true
    },
    [queryInstallmentDetails.fulfilled]: (state, { payload }) => {
      const { campground, isNotProduction, terminal } = payload
      state.campground.electricFeeConfig = campground.electricFeeConfigLegacy
      state.campground.hasLateFeeConfig = campground.hasLateFeeConfig
      state.campground.lateFeeConfig = campground.lateFeeConfig
      const isTerminalSimulation = isNotProduction
      const terminalReaderKey = campground.stripeTerminalReader?.key
      state.campground.terminal = configureTerminal({ ...terminal, isTerminalSimulation, terminalReaderKey })
      state.installmentDetails.data = campground.installment
      state.installmentDetails.error = null
      state.installmentDetails.loading = false
      state.installmentDetails.fetchDetails = false
    },
    [queryInstallmentDetails.rejected]: (state, { payload }) => {
      state.installmentDetails.error = payload
      state.installmentDetails.loading = false
      state.installmentDetails.fetchDetails = false
    },
    [queryInstallmentChangeOwed.pending]: state => {
      state.installmentDetails.action = 'getChangeOwed'
      state.installmentDetails.error = null
      state.installmentDetails.loading = true
    },
    [queryInstallmentChangeOwed.fulfilled]: (state, { payload }) => {
      state.installmentDetails.changeOwed = payload.camperChangeOwed
      state.installmentDetails.loading = false
    },
    [queryInstallmentChangeOwed.rejected]: (state, { payload }) => {
      state.installmentDetails.error = payload
      state.installmentDetails.loading = false
    },
    [queryInstallmentReceipt.fulfilled]: (state, { payload }) => {
      state.installmentDetails.data = payload
      state.installmentDetails.error = null
      state.installmentDetails.loading = false
    },
    [queryInstallmentReceipt.rejected]: (state, { error }) => {
      state.installmentDetails.error = error
      state.installmentDetails.loading = false
    },
    [queryInstallmentReceipt.pending]: state => {
      state.installmentDetails.loading = true
    },
    [mutationInstallmentState.fulfilled]: (state, { payload }) => {
      const { action, installment } = payload
      if (action) state.installmentDetails.action = action
      state.installmentDetails.data = installment
      state.installmentDetails.error = null
      state.installmentDetails.loading = false
    },
    [mutationInstallmentState.rejected]: (state, { payload }) => {
      state.installmentDetails.error = payload
      state.installmentDetails.loading = false
    },
    [mutationInstallmentState.pending]: state => {
      state.installmentDetails.loading = true
    },
    [mutationInstallmentItemDelete.pending]: state => {
      state.installmentDetails.loading = true
    },
    [mutationInstallmentItemDelete.fulfilled]: (state, { payload }) => {
      state.installmentDetails.action = 'deleteItem'
      state.installmentDetails.data = payload
      state.installmentDetails.error = null
      state.installmentDetails.loading = false
    },
    [mutationInstallmentItemDelete.rejected]: (state, { payload }) => {
      state.installmentDetails.error = payload
      state.installmentDetails.loading = false
    },
    [mutationInstallmentPayment.pending]: state => {
      state.installmentDetails.error = null
      state.installmentDetails.loading = true
    },
    [mutationInstallmentPayment.fulfilled]: (state, { payload }) => {
      state.installmentDetails.action = 'payInvoice'
      state.installmentDetails.data = payload
      state.installmentDetails.error = null
      state.installmentDetails.loading = false
    },
    [mutationInstallmentPayment.rejected]: (state, { payload }) => {
      state.installmentDetails.error = payload
      state.installmentDetails.loading = false
    },
    [mutationInstallmentAttachEmailAndSend.fulfilled]: (state, { payload }) => {
      const { action, installment } = payload
      if (action) state.installmentDetails.action = action
      state.installmentDetails.data = installment
      state.installmentDetails.error = null
      state.installmentDetails.loading = false
    },
    [mutationInstallmentAttachEmailAndSend.rejected]: (state, { payload }) => {
      state.installmentDetails.error = payload
      state.installmentDetails.loading = false
    },
    [mutationInstallmentAttachEmailAndSend.pending]: state => {
      state.installmentDetails.error = null
      state.installmentDetails.loading = true
    },
  },
})

export const {
  fetchInstallmentStatus,
  setInvoiceReplacedStatus,
  setItem,
  updateAction,
  updateFilterInvoiceStatus,
  updateFilterPayoutStatus,
  updateFilterTerm,
  updateInstallmentDetailsData,
  updatePageData,
  updateParams,
  updateSortField,
} = invoices.actions

export default invoices.reducer
